'use client';

import React from 'react';
import { LocalUserChoices } from '@livekit/components-react';
import { ConnectionDetails } from '@/lib/types';
import { VideoConferenceComponent } from '@/app/rooms/[roomName]/PageClientImpl';
import { useMeetingI18n } from '@/lib/meeting-i18n';
import { disabledInitialMediaChoices, getInitialMediaChoices } from '@/lib/media-device-choices';
import styles from '@/styles/ManagedMeeting.module.css';

type ManagedMeeting = {
  slug: string;
  title: string;
  roomName: string;
  meetingUrl: string;
  requiresPassword: boolean;
  maxParticipants: number;
};

export function ManagedMeetingPage({ slug }: { slug: string }) {
  const t = useMeetingI18n();
  const [meeting, setMeeting] = React.useState<ManagedMeeting | undefined>(undefined);
  const [connectionDetails, setConnectionDetails] = React.useState<ConnectionDetails | undefined>(
    undefined,
  );
  const [userChoices, setUserChoices] = React.useState<LocalUserChoices | undefined>(undefined);
  const [name, setName] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetch(`/api/managed-meetings/${encodeURIComponent(slug)}`, { cache: 'no-store' })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(response.status === 404 ? t.meetingNotFound : await response.text());
        }
        return response.json() as Promise<ManagedMeeting>;
      })
      .then((data) => {
        if (!cancelled) {
          setMeeting(data);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message || t.unavailableMeeting);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, t.meetingNotFound, t.unavailableMeeting]);

  async function joinMeeting(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!meeting) {
      return;
    }
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError(t.nameRequired);
      return;
    }
    setSubmitting(true);
    setError('');
    const response = await fetch(`/api/managed-meetings/${encodeURIComponent(meeting.slug)}/join`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name: trimmedName, password }),
    });
    if (!response.ok) {
      setError(response.status === 401 ? t.incorrectPassword : await response.text());
      setSubmitting(false);
      return;
    }
    const details = (await response.json()) as ConnectionDetails;
    let mediaChoices = disabledInitialMediaChoices;
    try {
      const devices = await navigator.mediaDevices?.enumerateDevices();
      if (devices) {
        mediaChoices = getInitialMediaChoices(devices);
      }
    } catch (deviceError) {
      console.warn('Could not enumerate media devices before joining', deviceError);
    }
    setUserChoices({
      username: trimmedName,
      ...mediaChoices,
    });
    setConnectionDetails(details);
    setSubmitting(false);
  }

  if (connectionDetails && userChoices) {
    return (
      <main data-lk-theme="default" style={{ height: '100%' }}>
        <VideoConferenceComponent
          connectionDetails={connectionDetails}
          userChoices={userChoices}
          options={{ codec: 'vp9', hq: false, singlePeerConnection: true }}
        />
      </main>
    );
  }

  return (
    <main className={styles.shell}>
      <button
        className={styles.language}
        type="button"
        onClick={t.switchLocale}
        aria-label={t.toggleLocaleLabel}
      >
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="9" strokeWidth="1.7" />
          <path
            d="M3 12H21M12 3C14.4 5.45 15.6 8.45 15.6 12S14.4 18.55 12 21M12 3C9.6 5.45 8.4 8.45 8.4 12S9.6 18.55 12 21"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
        </svg>
        {t.localeLabel}
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M7 10L12 15L17 10"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <section className={styles.hero}>
        <div className={styles.brand}>
          <img className={styles.brandLogo} src="/brand/logo.png" alt="logo" />
          <span className={styles.brandTitle}>Meeting</span>
        </div>

        <div className={styles.tagline}>{t.tagline}</div>

        <section className={styles.card}>
          <div className={styles.avatar}>
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M20 21C20 17.686 16.866 15 13 15H11C7.134 15 4 17.686 4 21"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <circle cx="12" cy="7" r="4" strokeWidth="1.8" />
            </svg>
          </div>
          <h2 className={styles.title}>{t.joinTitle}</h2>
          <p className={styles.meta}>
            {loading ? t.loadingMeeting : meeting ? t.joinMeta : t.unavailableMeeting}
          </p>

          {error && <p className={styles.error}>{error}</p>}
          {meeting && (
            <form className={styles.form} onSubmit={joinMeeting}>
              <label className={styles.field}>
                <span className={styles.srOnly}>{t.name}</span>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M20 21C20 17.686 16.866 15 13 15H11C7.134 15 4 17.686 4 21"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                  <circle cx="12" cy="7" r="4" strokeWidth="1.8" />
                </svg>
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  autoComplete="name"
                  placeholder={t.namePlaceholder}
                  required
                />
              </label>
              {meeting.requiresPassword && (
                <label className={styles.field}>
                  <span className={styles.srOnly}>{t.password}</span>
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="4" y="10" width="16" height="10" rx="2" strokeWidth="1.8" />
                    <path
                      d="M8 10V7C8 4.8 9.8 3 12 3S16 4.8 16 7V10"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                  <input
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    type="password"
                    autoComplete="current-password"
                    placeholder={t.passwordPlaceholder}
                    required
                  />
                </label>
              )}
              <button className={styles.button} type="submit" disabled={submitting}>
                {submitting ? t.joining : t.joinMeeting}
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M5 12H19M13 6L19 12L13 18"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </form>
          )}
        </section>

        <section className={styles.features} aria-label={t.featuresLabel}>
          <div className={styles.feature}>
            <div className={styles.featureIcon}>
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M12 3L19 6V11C19 15.5 16.1 19.6 12 21C7.9 19.6 5 15.5 5 11V6L12 3Z"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 12L11 14L15.5 9.5"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <strong>{t.secureFeatureTitle}</strong>
              <span>{t.secureFeatureText}</span>
            </div>
          </div>
          <div className={styles.feature}>
            <div className={styles.featureIcon}>
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M13 2L5 13H12L11 22L19 10H12L13 2Z"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <strong>{t.lowLatencyFeatureTitle}</strong>
              <span>{t.lowLatencyFeatureText}</span>
            </div>
          </div>
          <div className={styles.feature}>
            <div className={styles.featureIcon}>
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 19V13M12 19V5M19 19V9" strokeWidth="1.9" strokeLinecap="round" />
              </svg>
            </div>
            <div>
              <strong>{t.qualityFeatureTitle}</strong>
              <span>{t.qualityFeatureText}</span>
            </div>
          </div>
        </section>
      </section>
    </main>
  );
}
